import Vue from 'vue';
import createAuth0Client from '@auth0/auth0-spa-js';
import SessionController from '@/controllers/SessionController';
import queryString from 'query-string';
import store from '../store';

function addUserbackScript(username) {
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://static.userback.io/widget/v1.js';
  script.onload = () => {
    window.Userback.custom_data = {
      username,
    };
  };
  document.head.appendChild(script);
}

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

let instance;

/** Returns the current instance of the SDK */
export const getInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  router,
}) => {
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        error: null,
      };
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      const { data: keys, error } = await SessionController.getAuth0Keys();
      if (keys) {
        try {
          // Create a new instance of the SDK client using members of the given options object
          this.auth0Client = await createAuth0Client({
            domain: keys.domain,
            client_id: keys.clientId,
            audience: keys.audience,
            redirect_uri: redirectUri,
          });
          // If the user is returning to the app after authentication..
          if (window.location.search.includes('error=')) {
            ({ error: this.error, error_description: this.errorDescription } = queryString.parse(
              window.location.search
            ));
          }
          if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
            // handle the redirect and retrieve tokens
            const { appState } = await this.auth0Client.handleRedirectCallback();

            this.error = null;

            // Notify subscribers that the redirect callback has happened, passing the appState
            // (useful for retrieving any pre-authentication state)
            onRedirectCallback(appState);
          }
        } catch (e) {
          this.error = e;
          if (e.response && e.response.data && e.response.data.error && window.location.pathname !== '/auth') {
            router.push({ path: '/auth' });
          }
        } finally {
          if (this.auth0Client) {
            this.isAuthenticated = await this.auth0Client.isAuthenticated();
            this.user = await this.auth0Client.getUser();
            addUserbackScript(this.user);
          }
          this.loading = false;
        }
      } else {
        this.error = error ?? 'Unable to get auth keys';
      }

      if (!this.error) {
        store.dispatch('session/setError', this.store);
      }
    },
    methods: {
      /** Handles the callback when logging in using a redirect */
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.isAuthenticated = true;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(options) {
        localStorage.removeItem('login-token');
        return this.auth0Client.loginWithRedirect(options);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims(options) {
        return this.auth0Client.getIdTokenClaims(options);
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      getTokenSilently(options) {
        return this.auth0Client.getTokenSilently(options);
      },
      /** Logs the user out and removes their session on the authorization server */
      logout(options) {
        // TODO: fix this, seems to be buggy, we should not need to pass in redirectUri
        return this.auth0Client.logout({ ...options, returnTo: redirectUri });
      },
    },
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  },
};
